// 참조 데이터 Context - 모든 inf_ 테이블 데이터를 앱 전체에서 공유
import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { ReferenceData, InfStat } from '../types';
import { referenceApi } from '../services/api';
import { message } from 'antd';

interface ReferenceDataContextType {
  data: ReferenceData | null;
  loading: boolean;
  error: string | null;
  reloadReferenceData: () => Promise<void>;
  getSubtypesByParent: (parentType: string) => any[];
  getStatsByCategory: (category?: string, isFromAffected?: boolean) => InfStat[];
}

const ReferenceDataContext = createContext<ReferenceDataContextType | undefined>(undefined);

interface ReferenceDataProviderProps {
  children: ReactNode;
}

export const ReferenceDataProvider: React.FC<ReferenceDataProviderProps> = ({ children }) => {
  const [data, setData] = useState<ReferenceData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadReferenceData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // 더 이상 getAllReferenceData를 사용하지 않음
      // 각 컴포넌트가 필요한 데이터를 직접 로드하도록 변경됨
      console.warn('ReferenceDataContext is deprecated. Components should load their own reference data.');
      
      // 빈 데이터로 초기화
      setData({
        skillTypes: [],
        targetTypes: [],
        skillAreas: [],
        interruptibles: [],
        effectTypes: [],
        effectAffects: [],
        effectDispels: [],
        durationTypes: [],
        powerTypes: [],
        stats: [],
        calculationMethods: [],
        difficulties: [],
        mechanismTypes: []
      });
      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : '참조 데이터 로드 실패';
      setError(errorMessage);
      console.error('참조 데이터 로드 오류:', err);
    } finally {
      setLoading(false);
    }
  };

  const reloadReferenceData = async () => {
    await loadReferenceData();
  };

  // 부모 타입에 따른 서브타입 필터링 (현재 미사용)
  const getSubtypesByParent = (parentType: string) => {
    return [];
  };

  // 카테고리별 스탯 필터링
  const getStatsByCategory = (category?: string, isFromAffected?: boolean) => {
    if (!data?.stats) return [];
    
    let filteredStats = data.stats;
    
    // 카테고리 필터링
    if (category) {
      filteredStats = filteredStats.filter(stat => stat.stat_category === category);
    }
    
    // is_from_affected에 따른 필터링
    if (isFromAffected !== undefined) {
      if (isFromAffected) {
        // 대상의 스탯만 (TARGET_ 접두어)
        filteredStats = filteredStats.filter(stat => stat.stat_code.startsWith('TARGET_'));
      } else {
        // 시전자의 스탯만 (TARGET_ 접두어 제외)
        filteredStats = filteredStats.filter(stat => !stat.stat_code.startsWith('TARGET_'));
      }
    }
    
    return filteredStats.sort((a, b) => a.sort_order - b.sort_order);
  };

  useEffect(() => {
    loadReferenceData();
  }, []);

  const contextValue: ReferenceDataContextType = {
    data,
    loading,
    error,
    reloadReferenceData,
    getSubtypesByParent,
    getStatsByCategory,
  };

  return (
    <ReferenceDataContext.Provider value={contextValue}>
      {children}
    </ReferenceDataContext.Provider>
  );
};

export const useReferenceData = (): ReferenceDataContextType => {
  const context = useContext(ReferenceDataContext);
  if (context === undefined) {
    throw new Error('useReferenceData must be used within a ReferenceDataProvider');
  }
  return context;
};

// 개별 참조 데이터 hook들
export const useSkillTypes = () => {
  const { data } = useReferenceData();
  return data?.skillTypes || [];
};

export const useTargetTypes = () => {
  const { data } = useReferenceData();
  return data?.targetTypes || [];
};

export const useSkillAreas = () => {
  const { data } = useReferenceData();
  return data?.skillAreas || [];
};

export const useInterruptibles = () => {
  const { data } = useReferenceData();
  return data?.interruptibles || [];
};

export const useEffectTypes = () => {
  const { data } = useReferenceData();
  return data?.effectTypes || [];
};

export const useEffectAffects = () => {
  const { data } = useReferenceData();
  return data?.effectAffects || [];
};

export const useEffectDispels = () => {
  const { data } = useReferenceData();
  return data?.effectDispels || [];
};

export const useDurationTypes = () => {
  const { data } = useReferenceData();
  return data?.durationTypes || [];
};

export const usePowerTypes = () => {
  const { data } = useReferenceData();
  return data?.powerTypes || [];
};

export const useStats = () => {
  const { data } = useReferenceData();
  return data?.stats || [];
};

export const useCalculationMethods = () => {
  const { data } = useReferenceData();
  return data?.calculationMethods || [];
};


export const useDifficulties = () => {
  const { data } = useReferenceData();
  return data?.difficulties || [];
};

export const useMechanismTypes = () => {
  const { data } = useReferenceData();
  return data?.mechanismTypes || [];
};

export default ReferenceDataContext;